/**
 * SnapshotMismatchAuditPanel — May 2026
 *
 * Admin dashboard showing notes where the chart-context snapshot
 * renderer (medication / diagnosis / vitals) detected that the
 * saved snapshot belonged to a different patient than the note's
 * client.
 *
 * Source events come from
 * ``POST /api/admin/snapshot-mismatch-events`` (fired automatically
 * by ``SnapshotMismatchBanner`` whenever a clinician opens an
 * affected note).
 *
 * Compliance use-case: surface notes that still have stale corrupted
 * data, so an admin can either (a) reach out to the clinician who
 * created the note to re-save it, or (b) mark the event resolved
 * once they've verified the heal.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, RefreshCw, Check, ShieldAlert, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { formatUtcTimestamp } from '../../utils/utcTime';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const _FIELD_LABEL = {
  client_medications: 'Medications',
  client_diagnoses:   'Diagnoses',
  client_vitals:      'Vitals',
};

const SnapshotMismatchAuditPanel = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [daysBack, setDaysBack] = useState(30);
  const [includeResolved, setIncludeResolved] = useState(false);
  const [resolvingKey, setResolvingKey] = useState(null);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await axios.get(`${API}/admin/snapshot-mismatch-events`, {
        params: { days_back: daysBack, include_resolved: includeResolved },
      });
      setEvents(resp.data?.events || []);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error(`Failed to load mismatch events: ${detail}`);
    } finally {
      setLoading(false);
    }
  }, [daysBack, includeResolved]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handleResolve = useCallback(async (dedupKey) => {
    setResolvingKey(dedupKey);
    try {
      await axios.post(`${API}/admin/snapshot-mismatch-events/${dedupKey}/resolve`);
      toast.success('Event marked resolved');
      // Drop the resolved event from the list (unless we're showing
      // resolved events too).
      if (includeResolved) {
        setEvents(prev => prev.map(e => (e.dedup_key === dedupKey ? { ...e, resolved: true } : e)));
      } else {
        setEvents(prev => prev.filter(e => e.dedup_key !== dedupKey));
      }
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error(`Resolve failed: ${detail}`);
    } finally {
      setResolvingKey(null);
    }
  }, [includeResolved]);

  return (
    <Card data-testid="snapshot-mismatch-audit-panel" className="border-amber-200">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-amber-600" />
            <CardTitle className="text-base">Snapshot Mismatch Audit</CardTitle>
            <Badge variant="outline" className="text-xs">PHI compliance</Badge>
          </div>
          <div className="flex items-center gap-2">
            <Select
              value={String(daysBack)}
              onValueChange={(v) => setDaysBack(Number(v))}
              data-testid="snapshot-audit-days-select"
            >
              <SelectTrigger className="w-[140px] h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
                <SelectItem value="365">Last 365 days</SelectItem>
              </SelectContent>
            </Select>
            <label className="flex items-center gap-1 text-xs text-gray-600 select-none">
              <input
                type="checkbox"
                checked={includeResolved}
                onChange={(e) => setIncludeResolved(e.target.checked)}
                data-testid="snapshot-audit-include-resolved"
                className="h-3 w-3"
              />
              Show resolved
            </label>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchEvents}
              disabled={loading}
              data-testid="snapshot-audit-refresh-btn"
              className="h-8 text-xs"
            >
              <RefreshCw className={`h-3 w-3 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-6 text-center text-sm text-gray-500">
            <Loader2 className="h-4 w-4 inline animate-spin mr-2" />
            Loading mismatch events…
          </div>
        ) : events.length === 0 ? (
          <div className="py-6 text-center text-sm text-gray-500" data-testid="snapshot-audit-empty">
            <Check className="h-5 w-5 text-green-600 inline mr-2" />
            No snapshot mismatches detected in the last {daysBack} days.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="border-b bg-gray-50">
                <tr>
                  <th className="text-left p-2 font-semibold">Detected</th>
                  <th className="text-left p-2 font-semibold">Field</th>
                  <th className="text-left p-2 font-semibold">Note's Client</th>
                  <th className="text-left p-2 font-semibold">Wrong Client Stamped</th>
                  <th className="text-left p-2 font-semibold">Occurrences</th>
                  <th className="text-left p-2 font-semibold">Reported By</th>
                  <th className="text-left p-2 font-semibold w-32">Action</th>
                </tr>
              </thead>
              <tbody>
                {events.map((e) => {
                  const lastSeen = e.last_seen_at ? formatUtcTimestamp(e.last_seen_at) : '—';
                  return (
                    <tr key={e.dedup_key} className="border-b last:border-0 hover:bg-amber-50/40">
                      <td className="p-2 whitespace-nowrap text-gray-700" data-testid={`snapshot-audit-row-${e.dedup_key}-when`}>
                        {lastSeen}
                      </td>
                      <td className="p-2">
                        <Badge variant="outline" className="text-xs">
                          {_FIELD_LABEL[e.field_type] || e.field_type}
                        </Badge>
                      </td>
                      <td className="p-2 text-gray-900">
                        {e.expected_client_name || (
                          <span className="text-gray-400 italic">unknown</span>
                        )}
                      </td>
                      <td className="p-2 text-red-700">
                        {(e.detected_client_names || []).length > 0
                          ? e.detected_client_names.join(', ')
                          : <span className="text-gray-400 italic">unknown</span>}
                      </td>
                      <td className="p-2 text-gray-700">
                        {e.occurrence_count || 1}
                      </td>
                      <td className="p-2 text-gray-700 truncate max-w-[200px]" title={e.last_reporter_email || ''}>
                        {e.last_reporter_name || e.last_reporter_email || '—'}
                      </td>
                      <td className="p-2">
                        {e.resolved ? (
                          <Badge className="bg-green-100 text-green-800 text-xs">Resolved</Badge>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 text-xs"
                            disabled={resolvingKey === e.dedup_key}
                            onClick={() => handleResolve(e.dedup_key)}
                            data-testid={`snapshot-audit-resolve-btn-${e.dedup_key}`}
                          >
                            {resolvingKey === e.dedup_key ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : 'Mark resolved'}
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="mt-3 flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <div>
                <strong>How to heal a note:</strong> open the affected note and
                re-save it. The auto-populate path re-stamps the snapshot
                from the correct client's current chart data, the renderer
                drops the corrupted snapshot, and the event row stops
                surfacing here on the next refresh.
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SnapshotMismatchAuditPanel;
